import React, { useState } from 'react';
import { database, ref, push, set, get } from '../../firebase';
import { Send, ArrowLeft, ChevronDown, Monitor, CreditCard, Wallet, Lock, TrendingUp, Users, HelpCircle } from 'lucide-react';

const CreateTicket = () => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [problemType, setProblemType] = useState('');
  const [priority, setPriority] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [showProblemDropdown, setShowProblemDropdown] = useState(false);

  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');
  const sponsorId = localStorage.getItem('sponsorId');

  const problemTypes = [
    { value: 'technical', label: 'Technical Issue', icon: Monitor },
    { value: 'payment', label: 'Payment Problem', icon: CreditCard },
    { value: 'withdrawal', label: 'Withdrawal Issue', icon: Wallet },
    { value: 'account', label: 'Account Access', icon: Lock },
    { value: 'investment', label: 'Investment Query', icon: TrendingUp },
    { value: 'referral', label: 'Referral Issue', icon: Users },
    { value: 'other', label: 'Other', icon: HelpCircle }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'blue' },
    { value: 'medium', label: 'Medium', color: 'yellow' },
    { value: 'high', label: 'High', color: 'orange' },
    { value: 'urgent', label: 'Urgent', color: 'red' }
  ];

  const selectedProblem = problemTypes.find(p => p.value === problemType);
  const SelectedIcon = selectedProblem?.icon || HelpCircle;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userId || !subject || !message || !problemType) {
      alert('Please fill all required fields');
      return;
    }

    setLoading(true);

    try {
      const today = new Date().toISOString().split('T')[0];
      const userKey = sponsorId || userId;
      const ticketsRef = ref(database, `UserTicket/${today}/${userKey}`);
      const snapshot = await get(ticketsRef);
      let ticketCount = 0;
      if (snapshot.exists()) {
        ticketCount = Object.keys(snapshot.val()).length;
      }
      
      const dateStr = today.replace(/-/g, '');
      const ticketNum = `TKT-${dateStr}-${String(ticketCount + 1).padStart(3, '0')}`;
      
      const ticketData = {
        ticketId: ticketNum,
        userId: userId,
        userName: userName || 'User',
        sponsorId: sponsorId || userId,
        subject: subject,
        description: message,
        problemType: problemType,
        priority: priority,
        status: 'open',
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        replies: []
      };
      
      const newTicketRef = push(ref(database, `UserTicket/${today}/${userKey}`));
      await set(newTicketRef, ticketData);
      
      setTicketId(ticketNum);
      setShowSuccess(true);
      
      setSubject('');
      setMessage('');
      setProblemType('');
      setPriority('medium');
      
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Failed to create ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="px-3 pt-2 pb-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <button 
            onClick={() => window.history.back()}
            className="p-1.5 hover:bg-secondary rounded-lg transition"
          >
            <ArrowLeft size={18} className="text-foreground" />
          </button>
          <div>
            <h1 className="text-base font-semibold text-foreground">Create Ticket</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Get help from support team</p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-card p-3.5 rounded-xl">
          <form onSubmit={handleSubmit} className="space-y-3.5">
            
            {/* Problem Type - Dropdown */}
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">
                Problem Type *
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowProblemDropdown(!showProblemDropdown)}
                  className="w-full bg-background text-foreground text-sm px-4 py-3 rounded-lg border border-border focus:border-primary focus:outline-none flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    {selectedProblem ? (
                      <>
                        <SelectedIcon size={16} className="text-foreground" />
                        <span>{selectedProblem.label}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">Select problem type</span>
                    )}
                  </span>
                  <ChevronDown size={16} className={`text-muted-foreground transition-transform ${showProblemDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showProblemDropdown && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowProblemDropdown(false)}></div>
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
                      {problemTypes.map(type => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => {
                              setProblemType(type.value);
                              setShowProblemDropdown(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-secondary transition-colors ${
                              problemType === type.value ? 'bg-primary/10' : ''
                            }`}
                          >
                            <Icon size={16} className="text-foreground" />
                            <span className="text-sm text-foreground">{type.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Priority Selection */}
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Priority *</label>
              <div className="flex flex-wrap gap-1.5">
                {priorities.map(p => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPriority(p.value)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-medium border transition-all ${
                      priority === p.value
                        ? `bg-${p.color}-500/20 border-${p.color}-500 text-${p.color}-400`
                        : 'border-border bg-background text-muted-foreground'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">
                Subject *
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of your issue"
                className="w-full bg-background text-foreground text-sm px-4 py-3 rounded-lg border border-border focus:border-primary focus:outline-none"
                required
              />
            </div>
            
            {/* Message */}
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">
                Message *
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe your issue in detail..."
                rows={4}
                className="w-full bg-background text-foreground text-sm px-4 py-3 rounded-lg border border-border focus:border-primary focus:outline-none resize-none"
                required
              />
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Send size={14} />
                  <span>Create Ticket</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Quick Tips */}
        <div className="mt-3 bg-card/50 border border-border rounded-lg p-3.5">
          <h3 className="text-xs font-medium text-foreground mb-2">📝 Quick Tips:</h3>
          <ul className="text-[10px] text-muted-foreground space-y-1">
            <li>• Be specific about your issue</li>
            <li>• Include relevant transaction IDs if any</li>
            <li>• Our team typically responds within 24 hours</li>
            <li>• You'll get email notifications on replies</li>
          </ul>
        </div>

        {/* Success Modal */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-xl p-5 max-w-sm w-full shadow-xl">
              <div className="text-center">
                <div className="h-14 w-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="h-7 w-7 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-foreground mb-1">Ticket Created!</h3>
                <p className="text-xs text-muted-foreground mb-2">Your support ticket has been created</p>
                <p className="text-[10px] bg-primary/10 text-primary p-2 rounded-lg mb-3 font-mono">
                  ID: {ticketId}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowSuccess(false)}
                    className="flex-1 py-2.5 border border-border rounded-lg text-xs font-medium hover:bg-secondary/50 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => window.location.href = '/user-dashboard/support'}
                    className="flex-1 py-2.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/80 transition-colors"
                  >
                    View Tickets
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateTicket;